/* eslint-disable complexity */
import { usePreloadedQuery, type PreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { parseRoute } from "src/wac-routes";

import { agileTutorialOneQuery$data } from "@/__generated_contentful__/agileTutorialOneQuery.graphql";
import { agileTutorialOneQuery } from "@/__generated_contentful__/agileTutorialOneQuery.graphql";
import { agileTutorialTwoQuery$data } from "@/__generated_contentful__/agileTutorialTwoQuery.graphql";
import { AgileTutorial } from "@/templates/agile-hub/tutorial-types";
import { WAC_TO_CPAPI_LOCALE_MAP } from "@/utils/content-queries/constants";

import { richTextToHTML } from "../../clients/uniform-client/enhancers/utils";
import {
    CallToAction,
    TextComponent,
    AssetComponent,
    ProTipComponent,
    TwitterComponent,
    CallOutComponent,
    QuestionComponent,
    NextBestAction
} from "../../templates/agile-hub/types";

import type { OperationType } from "relay-runtime";

type Merge<T, U> = T & U;
type Combined = Merge<
    NonNullable<
        NonNullable<
            agileTutorialOneQuery$data["tutorialDetailsCollection"]
        >["items"]
    >[number],
    NonNullable<
        NonNullable<
            agileTutorialTwoQuery$data["tutorialDetailsCollection"]
        >["items"]
    >[number]
>;
export type AgileTutorialQueryData = {
    readonly tutorialDetailsCollection: {
        items: ReadonlyArray<Combined>;
    };
};

export const AgileTutorialOneQuery = graphql`
    query agileTutorialOneQuery(
        $limit: Int = 1
        $urlSlug: String!
        $locale: String!
    ) {
        tutorialDetailsCollection(
            limit: $limit
            where: { tutorialRef: { urlSlug: $urlSlug } }
            locale: $locale
        ) {
            items {
                tutorialRef {
                    title: tutorialTitle
                    tutorialName: tutorialNameExternal
                    description: tutorialDescription
                    urlSlug
                    tutorialBanner {
                        title
                        description
                        url
                        width
                        height
                        size
                        fileName
                        contentType
                    }
                }
                subtitle: tutorialSubtitle
                author {
                    name: authorNameExternal
                    jobTitle
                    authorPicture {
                        title
                        description
                        url
                        width
                        height
                        size
                        fileName
                        contentType
                    }
                    shortBiography
                    organization {
                        name: organizationNameExternal
                        tagline
                        logoCollection(limit: 1) {
                            items {
                                title
                                description
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                        }
                        shortDescription
                        industryCollection(limit: 1) {
                            items {
                                name: industryNameExternal
                                shortDescription
                            }
                        }
                        organizationSize {
                            name: companySizeTitleExternal
                            shortDescription
                        }
                        region {
                            name: regionNameExternal
                            shortDescription
                        }
                    }
                }
                tutorialSummary {
                    json
                }
                tutorialIntroductionCollection(limit: 1) {
                    items {
                        tutorialIntroductionName: tutorialIntroductionTitleExternal
                        tutorialIntroductionDetails {
                            json
                        }
                        tutorialIntroductionAsset {
                            title
                            description
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        embedLink
                    }
                }
                beforeYouBeginCollection(limit: 1) {
                    items {
                        beforeYouBeginTitle: beforeYouBeginTitleExternal
                        time
                        audience {
                            json
                        }
                        prerequisite {
                            json
                        }
                        ctaMicrocopy {
                            ctaMicrocopyTitle: ctaMicrocopyTitleExternal
                            ctaButtonText
                            ctaUrl
                        }
                        relatedAsset {
                            title
                            description
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        relatedQuestions {
                            questionTitle: questionTitleExternal
                            answer {
                                json
                            }
                            answerAsset {
                                title
                                description
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                        }
                    }
                }
                tutorialInstructionsCollection(limit: 1) {
                    items {
                        tutorialInstructionsTitle: tutorialInstructionsTitleExternal
                        tutorialInstructionStepsCollection {
                            items {
                                tutorialUseStepTitle: tutorialUseStepTitleExternal
                                tutorialBodyTextContainerCollection {
                                    items {
                                        ... on AssetComponent {
                                            __typename
                                            caption: assetCaption
                                            assetRelatedText {
                                                json
                                            }
                                            asset {
                                                title
                                                description
                                                url
                                                width
                                                height
                                                size
                                                fileName
                                                contentType
                                            }
                                        }
                                        ... on ProTipComponent {
                                            __typename
                                            name: proTipNameExternal
                                            proTipText
                                        }
                                        ... on TextComponent {
                                            __typename
                                            name: textComponentNameExternal
                                            text {
                                                json
                                            }
                                        }
                                        ... on TwitterComponent {
                                            __typename
                                            twitterComponentName: twitterComponentExternalName
                                            tweetText
                                            twitterUrl
                                        }
                                        ... on CallToAction {
                                            __typename
                                            title: ctaTitleExternal
                                            valueProposition
                                            ctaText
                                            url: ctaUrl
                                            asset: ctaAsset {
                                                title
                                                description
                                                url
                                                width
                                                height
                                                size
                                                fileName
                                                contentType
                                            }
                                            productCollection(limit: 1) {
                                                items {
                                                    productNameReference {
                                                        productName
                                                    }
                                                }
                                            }
                                            dataBlueprintModule
                                        }
                                        ... on QuestionComponent {
                                            __typename
                                            questionTitle: questionTitleExternal
                                            answer {
                                                json
                                            }
                                        }
                                        ... on CallOutComponent {
                                            __typename
                                            title: callOutComponentTitleExternal
                                            callOutText {
                                                json
                                            }
                                            icon: callOutComponentIcon {
                                                title
                                                description
                                                url
                                                width
                                                height
                                                size
                                                fileName
                                                contentType
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const AgileTutorialTwoQuery = graphql`
    query agileTutorialTwoQuery(
        $limit: Int = 1
        $urlSlug: String!
        $locale: String!
    ) {
        tutorialDetailsCollection(
            limit: $limit
            where: { tutorialRef: { urlSlug: $urlSlug } }
            locale: $locale
        ) {
            items {
                contentHubSubscribeCollection {
                    items {
                        subscribeComponentName: subscribeComponentNameExternal
                        valueProposition
                        ctaButtonText
                        successMessage
                    }
                }
                productDiscussedCtaCollection(limit: 1) {
                    items {
                        title: ctaTitleExternal
                        valueProposition
                        text: ctaText
                        url: ctaUrl
                        asset: ctaAsset {
                            title
                            description
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        productCollection(limit: 1) {
                            items {
                                productNameReference {
                                    productName
                                }
                                icon {
                                    title
                                    description
                                    url
                                    width
                                    height
                                    size
                                    fileName
                                    contentType
                                }
                            }
                        }
                        dataBlueprintModule
                    }
                }
                relatedTemplateCtaCollection {
                    items {
                        title: ctaTitleExternal
                        valueProposition
                        text: ctaText
                        url: ctaUrl
                        productCollection(limit: 1) {
                            items {
                                productNameReference {
                                    productName
                                }
                            }
                        }
                    }
                }
                whitePaperCtaCollection(limit: 1) {
                    items {
                        title: ctaTitleExternal
                        valueProposition
                        text: ctaText
                        url: ctaUrl
                        productCollection(limit: 1) {
                            items {
                                productNameReference {
                                    productName
                                }
                            }
                        }
                        dataBlueprintModule
                    }
                }
                upNext {
                    ... on MicrositeArticle {
                        __typename
                        articleName: articleNameExternal
                        articleTitle
                        description
                        articleBanner {
                            description
                            name: title
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        urlSlug
                    }
                    ... on Tutorial {
                        __typename
                        tutorialName: tutorialNameExternal
                        tutorialTitle
                        description: tutorialDescription
                        tutorialBanner {
                            description
                            name: title
                            url
                            width
                            height
                            size
                            fileName
                            contentType
                        }
                        urlSlug
                    }
                }
                nextBestActionCollection(limit: 2) {
                    items {
                        ... on MicrositeArticle {
                            __typename
                            articleName: articleNameExternal
                            title: articleTitle
                            description
                            articleBanner {
                                title
                                description
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                            urlSlug
                        }
                        ... on Tutorial {
                            __typename
                            name: tutorialNameExternal
                            title: tutorialTitle
                            description: tutorialDescription
                            tutorialBanner {
                                title
                                description
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                            urlSlug
                        }
                    }
                }
                shareThisTutorialCollection(limit: 1) {
                    items {
                        socialMediaHandle
                        socialMediaUrl
                        socialMediaChannel {
                            socialMediaChannel: socialMediaChannelExternal
                        }
                    }
                }
                relatedHubCollection(limit: 1) {
                    items {
                        title: contentHubTitleExternal
                        shortDescriptionOneLiner
                        description {
                            json
                        }
                    }
                }
                relatedProductFeaturesCollection(limit: 1) {
                    items {
                        _id
                        name: featureNameExternal
                        shortDescriptionOneLiner
                        description {
                            json
                        }
                    }
                }
            }
        }
    }
`;

export const agileTutorialImageFragment = graphql`
    fragment agileTutorialImage on Asset {
        title
        description
        url
        width
        height
        size
        fileName
        contentType
    }
`;

export const getAgileTutorialQueryVars = (location: Location) => {
    const { locale, path } = parseRoute(location);

    return {
        limit: 1,
        urlSlug: path ? `/${path}` : "",
        locale: WAC_TO_CPAPI_LOCALE_MAP[locale] || "en-US"
    };
};

export const shapeAgileHubTutorialContentfulResponse = (
    data: AgileTutorialQueryData
): AgileTutorial => {
    const tutorial = data.tutorialDetailsCollection?.items?.[0];

    if (!tutorial) {
        return {} as AgileTutorial;
    }

    return {
        subtitle: tutorial?.subtitle ?? "",
        author: {
            name: tutorial?.author?.name ?? "",
            jobTitle: tutorial?.author?.jobTitle ?? "",
            authorPicture: {
                name: tutorial?.author?.authorPicture?.title ?? "",
                description: tutorial?.author?.authorPicture?.description ?? "",
                url: tutorial?.author?.authorPicture?.url ?? "",
                width: tutorial?.author?.authorPicture?.width ?? 0,
                height: tutorial?.author?.authorPicture?.height ?? 0,
                size: tutorial?.author?.authorPicture?.size ?? 0,
                fileName: tutorial?.author?.authorPicture?.fileName ?? "",
                contentType: tutorial?.author?.authorPicture?.contentType ?? "",
                refreshUrl: tutorial?.author?.authorPicture?.url ?? ""
            },
            shortBiography: tutorial?.author?.shortBiography ?? ""
        },
        tutorialSummary: richTextToHTML(tutorial?.tutorialSummary?.json ?? {}),
        tutorialInstructions: (
            tutorial?.tutorialInstructionsCollection?.items ?? []
        ).map((item) => ({
            tutorialInstructionsTitle: item?.tutorialInstructionsTitle ?? "",
            tutorialInstructionSteps: item?.tutorialInstructionStepsCollection
                ?.items
                ? item.tutorialInstructionStepsCollection.items.map((step) => ({
                      tutorialUseStepTitle: step?.tutorialUseStepTitle ?? "",
                      tutorialBodyTextContainer: step
                          ?.tutorialBodyTextContainerCollection?.items
                          ? step.tutorialBodyTextContainerCollection.items.map(
                                (component) => {
                                    switch (component?.__typename) {
                                        case "TextComponent":
                                            return {
                                                __typename:
                                                    component.__typename,
                                                name: component.name || "",
                                                text: component.text?.json
                                                    ? richTextToHTML(
                                                          component.text?.json
                                                      )
                                                    : null
                                            } as TextComponent;
                                        case "AssetComponent":
                                            return {
                                                __typename:
                                                    component.__typename,
                                                asset: component.asset,
                                                caption:
                                                    component.caption || null,
                                                assetRelatedText: component
                                                    .assetRelatedText?.json
                                                    ? richTextToHTML(
                                                          component
                                                              .assetRelatedText
                                                              ?.json
                                                      )
                                                    : null
                                            } as AssetComponent;
                                        case "ProTipComponent":
                                            return {
                                                __typename:
                                                    component.__typename,
                                                name: component.name || "",
                                                proTipText:
                                                    component.proTipText || ""
                                            } as ProTipComponent;
                                        case "TwitterComponent":
                                            return {
                                                __typename:
                                                    component.__typename,
                                                twitterComponentName:
                                                    component.twitterComponentName ||
                                                    "",
                                                tweetText:
                                                    component.tweetText || "",
                                                twitterUrl:
                                                    component.twitterUrl || ""
                                            } as TwitterComponent;
                                        case "CallOutComponent":
                                            return {
                                                __typename:
                                                    "ContentPlatformCallOutComponent",
                                                title: component.title || "",
                                                callOutText: component
                                                    .callOutText?.json
                                                    ? richTextToHTML(
                                                          component.callOutText
                                                              ?.json
                                                      )
                                                    : null,
                                                asset: null,
                                                icon: component.icon || null
                                            } as CallOutComponent;
                                        case "CallToAction":
                                            return {
                                                __typename:
                                                    component?.__typename,
                                                title: component.title || null,
                                                valueProposition:
                                                    component.valueProposition ||
                                                    null,
                                                text: component.ctaText || null,
                                                url: component.url || null,
                                                asset: component.asset
                                                    ? {
                                                          description:
                                                              component.asset
                                                                  .description ||
                                                              undefined,
                                                          name:
                                                              component.asset
                                                                  .title || "",
                                                          url:
                                                              component.asset
                                                                  .url || "",
                                                          width:
                                                              component.asset
                                                                  .width ||
                                                              undefined,
                                                          height:
                                                              component.asset
                                                                  .height ||
                                                              undefined,
                                                          size:
                                                              component.asset
                                                                  .size || 0,
                                                          fileName:
                                                              component.asset
                                                                  .fileName ||
                                                              null,
                                                          contentType:
                                                              component.asset
                                                                  .contentType ||
                                                              undefined
                                                      }
                                                    : null,
                                                productName:
                                                    component.productCollection
                                                        ?.items[0]
                                                        ?.productNameReference
                                                        ?.productName || null,
                                                dataBlueprintModule:
                                                    component.dataBlueprintModule ||
                                                    null
                                            } as CallToAction;
                                        case "QuestionComponent":
                                            return {
                                                __typename: "QuestionComponent",
                                                questionTitle:
                                                    component.questionTitle ||
                                                    "",
                                                answer: component.answer?.json
                                                    ? richTextToHTML(
                                                          component.answer?.json
                                                      )
                                                    : null
                                            } as QuestionComponent;
                                        default:
                                            return {
                                                __typename:
                                                    component?.__typename,
                                                title: null,
                                                valueProposition: null,
                                                text: null,
                                                url: null,
                                                asset: null,
                                                productName: null,
                                                productLogo: null,
                                                dataBlueprintModule: null
                                            } as CallToAction;
                                    }
                                }
                            )
                          : []
                  }))
                : []
        })),
        contentHubSubscribe:
            tutorial?.contentHubSubscribeCollection?.items.map((item) => ({
                subscribeComponentName: item?.subscribeComponentName ?? "",
                valueProposition: item?.valueProposition ?? "",
                ctaButtonText: item?.ctaButtonText ?? "",
                successMessage: item?.successMessage ?? ""
            })) || [],
        productDiscussedCta:
            tutorial?.productDiscussedCtaCollection?.items.map((item) => ({
                title: item?.title ?? "",
                valueProposition: item?.valueProposition ?? "",
                text: item?.text ?? "",
                url: item?.url ?? "",
                asset: item?.asset
                    ? {
                          name: item.asset.title ?? "",
                          title: item.asset.title ?? "",
                          description: item.asset.description ?? "",
                          url: item.asset.url ?? "",
                          width: item.asset.width ?? 0,
                          height: item.asset.height ?? 0,
                          size: item.asset.size ?? 0,
                          fileName: item.asset.fileName ?? "",
                          contentType: item.asset.contentType ?? ""
                      }
                    : null,
                productName:
                    item?.productCollection?.items[0]?.productNameReference
                        ?.productName ?? "",
                productLogo: item?.productCollection?.items[0]?.icon
                    ? {
                          name:
                              item.productCollection.items[0].icon.title ?? "",
                          description:
                              item.productCollection.items[0].icon
                                  .description ?? "",
                          url: item.productCollection.items[0].icon.url ?? "",
                          width:
                              item.productCollection.items[0].icon.width ?? 0,
                          height:
                              item.productCollection.items[0].icon.height ?? 0,
                          size: item.productCollection.items[0].icon.size ?? 0,
                          fileName:
                              item.productCollection.items[0].icon.fileName ??
                              "",
                          contentType:
                              item.productCollection.items[0].icon
                                  .contentType ?? ""
                      }
                    : null,
                dataBlueprintModule: item?.dataBlueprintModule ?? ""
            })) || [],
        relatedTemplateCta: tutorial?.relatedTemplateCtaCollection?.items
            ? tutorial.relatedTemplateCtaCollection.items.map((item) => ({
                  title: item?.title ?? "",
                  valueProposition: item?.valueProposition ?? "",
                  text: item?.text ?? "",
                  url: item?.url ?? "",
                  productName:
                      item?.productCollection?.items[0]?.productNameReference
                          ?.productName ?? "",
                  asset: null,
                  productLogo: null,
                  dataBlueprintModule: null
              }))
            : null,
        whitePaperCta: null,
        upNext: tutorial?.upNext
            ? (() => {
                  switch (tutorial.upNext.__typename) {
                      case "MicrositeArticle":
                          return {
                              __typename: "MicrositeArticle",
                              articleName: tutorial.upNext.articleName || null,
                              articleTitle:
                                  tutorial.upNext.articleTitle || null,
                              description: tutorial.upNext.description || null,
                              urlSlug: tutorial.upNext.urlSlug || "",
                              articleBanner: tutorial.upNext.articleBanner
                                  ? {
                                        description:
                                            tutorial.upNext.articleBanner
                                                .description || undefined,
                                        name:
                                            tutorial.upNext.articleBanner
                                                .name || "",
                                        url:
                                            tutorial.upNext.articleBanner.url ||
                                            "",
                                        width:
                                            tutorial.upNext.articleBanner
                                                .width || undefined,
                                        height:
                                            tutorial.upNext.articleBanner
                                                .height || undefined,
                                        size:
                                            tutorial.upNext.articleBanner
                                                .size || 0,
                                        fileName:
                                            tutorial.upNext.articleBanner
                                                .fileName || null,
                                        contentType:
                                            tutorial.upNext.articleBanner
                                                .contentType || undefined
                                    }
                                  : null
                          };
                      case "Tutorial":
                          return {
                              __typename: "Tutorial",
                              tutorialName:
                                  tutorial.upNext.tutorialName || null,
                              tutorialTitle:
                                  tutorial.upNext.tutorialTitle || null,
                              description: tutorial.upNext.description || null,
                              urlSlug: tutorial.upNext.urlSlug || "",
                              tutorialBanner: tutorial.upNext.tutorialBanner
                                  ? {
                                        description:
                                            tutorial.upNext.tutorialBanner
                                                .description || undefined,
                                        name:
                                            tutorial.upNext.tutorialBanner
                                                .name || "",
                                        url:
                                            tutorial.upNext.tutorialBanner
                                                .url || "",
                                        width:
                                            tutorial.upNext.tutorialBanner
                                                .width || undefined,
                                        height:
                                            tutorial.upNext.tutorialBanner
                                                .height || undefined,
                                        size:
                                            tutorial.upNext.tutorialBanner
                                                .size || 0,
                                        fileName:
                                            tutorial.upNext.tutorialBanner
                                                .fileName || null,
                                        contentType:
                                            tutorial.upNext.tutorialBanner
                                                .contentType || undefined
                                    }
                                  : null
                          };
                      default:
                          return null;
                  }
              })()
            : null,
        // @ts-ignore
        title: tutorial?.tutorialRef?.title || null,
        description: tutorial?.tutorialRef?.description || null,
        urlSlug: tutorial?.tutorialRef?.urlSlug || "",
        nextBestAction: (tutorial?.nextBestActionCollection?.items
            ?.map((nextAction) => {
                if (!nextAction) return null;
                switch (nextAction.__typename) {
                    case "MicrositeArticle":
                        return {
                            __typename: "MicrositeArticle" as const,
                            articleName: nextAction.articleName || null,
                            title: nextAction.title || null,
                            description: nextAction.description || null,
                            urlSlug: nextAction.urlSlug || "",
                            articleBanner: nextAction.articleBanner
                                ? {
                                      description:
                                          nextAction.articleBanner
                                              .description || undefined,
                                      name:
                                          nextAction.articleBanner.title || "",
                                      url: nextAction.articleBanner.url || "",
                                      width:
                                          nextAction.articleBanner.width ||
                                          undefined,
                                      height:
                                          nextAction.articleBanner.height ||
                                          undefined,
                                      size: nextAction.articleBanner.size || 0,
                                      fileName:
                                          nextAction.articleBanner.fileName ||
                                          null,
                                      contentType:
                                          nextAction.articleBanner
                                              .contentType || undefined
                                  }
                                : null
                        } as NextBestAction;
                    case "Tutorial":
                        return {
                            __typename: "Tutorial" as const,
                            name: nextAction.name || null,
                            title: nextAction.title || null,
                            description: nextAction.description || null,
                            urlSlug: nextAction.urlSlug || "",
                            tutorialBanner: nextAction.tutorialBanner?.url
                                ? {
                                      description:
                                          nextAction.tutorialBanner
                                              .description || undefined,
                                      name:
                                          nextAction.tutorialBanner.title || "",
                                      url: nextAction.tutorialBanner.url || "",
                                      width:
                                          nextAction.tutorialBanner.width ||
                                          undefined,
                                      height:
                                          nextAction.tutorialBanner.height ||
                                          undefined,
                                      size: nextAction.tutorialBanner.size || 0,
                                      fileName:
                                          nextAction.tutorialBanner.fileName ||
                                          null,
                                      contentType:
                                          nextAction.tutorialBanner
                                              .contentType || undefined
                                  }
                                : null
                        } as NextBestAction;
                    default:
                        return null;
                }
            })
            ?.filter(Boolean) || null) as NextBestAction[] | null,
        relatedHub:
            tutorial?.relatedHubCollection?.items.map((related) => ({
                title: related?.title,
                shortDescriptionOneLiner: related?.shortDescriptionOneLiner,
                description: related?.description?.json
                    ? richTextToHTML(related?.description?.json)
                    : null
            })) || [],
        shareThisTutorial: null,
        relatedProductFeatures: null,
        tutorialName: tutorial?.tutorialRef?.tutorialName ?? "",
        tutorialBanner: tutorial?.tutorialRef?.tutorialBanner
            ? {
                  description:
                      tutorial?.tutorialRef?.tutorialBanner.description ||
                      undefined,
                  name: tutorial?.tutorialRef?.tutorialBanner.title || "",
                  url: tutorial?.tutorialRef?.tutorialBanner.url || "",
                  width:
                      tutorial?.tutorialRef?.tutorialBanner.width || undefined,
                  height:
                      tutorial?.tutorialRef?.tutorialBanner.height || undefined,
                  size: tutorial?.tutorialRef?.tutorialBanner.size || 0,
                  fileName:
                      tutorial?.tutorialRef?.tutorialBanner.fileName || null,
                  contentType:
                      tutorial?.tutorialRef?.tutorialBanner.contentType ||
                      undefined
              }
            : null,
        tutorialIntroduction: (() => {
            const mapped =
                tutorial?.tutorialIntroductionCollection?.items
                    .map((aIntro) => ({
                        tutorialIntroductionName:
                            aIntro?.tutorialIntroductionName || null,
                        tutorialIntroductionDetails: aIntro
                            ?.tutorialIntroductionDetails?.json
                            ? richTextToHTML(
                                  aIntro?.tutorialIntroductionDetails?.json
                              )
                            : null,
                        embedLink: aIntro?.embedLink || null,
                        tutorialIntroductionAsset: aIntro
                            ?.tutorialIntroductionAsset?.url
                            ? {
                                  description:
                                      aIntro?.tutorialIntroductionAsset
                                          ?.description || undefined,
                                  name:
                                      aIntro?.tutorialIntroductionAsset
                                          ?.title || "",
                                  url:
                                      aIntro?.tutorialIntroductionAsset?.url ||
                                      "",
                                  width:
                                      aIntro?.tutorialIntroductionAsset
                                          ?.width || undefined,
                                  height:
                                      aIntro?.tutorialIntroductionAsset
                                          ?.height || undefined,
                                  size:
                                      aIntro?.tutorialIntroductionAsset?.size ||
                                      0,
                                  fileName:
                                      aIntro?.tutorialIntroductionAsset
                                          ?.fileName || null,
                                  contentType:
                                      aIntro?.tutorialIntroductionAsset
                                          ?.contentType || undefined
                              }
                            : null,
                        componentizedIntroduction: null // Adjust if you have componentizedIntroductionCollection in your schema
                    }))
                    ?.filter(Boolean) || null;

            return mapped?.length ? mapped : null;
        })(),
        beforeYouBegin:
            (tutorial?.beforeYouBeginCollection?.items || []).length > 0
                ? tutorial?.beforeYouBeginCollection?.items.map((item) => ({
                      beforeYouBeginTitle: item?.beforeYouBeginTitle || null,

                      time: item?.time || null,
                      audience: item?.audience?.json
                          ? richTextToHTML(item.audience.json)
                          : null,
                      prerequisite: item?.prerequisite?.json
                          ? richTextToHTML(item.prerequisite.json)
                          : null,
                      ctaMicrocopy: {
                          ctaMicrocopyTitle:
                              item?.ctaMicrocopy?.ctaMicrocopyTitle || null,
                          ctaButtonText:
                              item?.ctaMicrocopy?.ctaButtonText || null,
                          ctaUrl: item?.ctaMicrocopy?.ctaUrl || null
                      },
                      relatedAsset: item?.relatedAsset?.url
                          ? {
                                description: item?.relatedAsset.description
                                    ? item.relatedAsset.description
                                    : undefined,
                                name: item?.relatedAsset.title || "",
                                url: item?.relatedAsset.url || "",
                                size: item?.relatedAsset.size || 0,
                                fileName: item?.relatedAsset.fileName || null,
                                details: {
                                    image: {
                                        width:
                                            item?.relatedAsset.width ||
                                            undefined,
                                        height:
                                            item?.relatedAsset.height ||
                                            undefined
                                    }
                                },
                                contentType:
                                    item?.relatedAsset.contentType === null
                                        ? undefined
                                        : item?.relatedAsset.contentType
                            }
                          : null,
                      relatedQuestions: {
                          questionTitle:
                              item?.relatedQuestions?.questionTitle ?? null,
                          answer: item?.relatedQuestions?.answer?.json
                              ? richTextToHTML(
                                    item.relatedQuestions.answer.json
                                )
                              : null,
                          answerAsset: item?.relatedQuestions?.answerAsset?.url
                              ? {
                                    description:
                                        item.relatedQuestions.answerAsset
                                            ?.description || undefined,
                                    name:
                                        item.relatedQuestions.answerAsset
                                            .title || "",
                                    url:
                                        item.relatedQuestions.answerAsset.url ||
                                        "",
                                    width:
                                        item.relatedQuestions.answerAsset
                                            .width || undefined,
                                    height:
                                        item.relatedQuestions.answerAsset
                                            .height || undefined,
                                    size:
                                        item.relatedQuestions.answerAsset
                                            .size || 0,
                                    fileName:
                                        item.relatedQuestions.answerAsset
                                            .fileName || null,
                                    contentType:
                                        item.relatedQuestions.answerAsset
                                            .contentType === null
                                            ? undefined
                                            : item.relatedQuestions.answerAsset
                                                  .contentType
                                }
                              : null
                      }
                  })) || null
                : null
    };
};

export const GetAgileTutorialTitleDescription = (
    queryRef: PreloadedQuery<OperationType>
) => {
    const data = usePreloadedQuery<agileTutorialOneQuery>(
        AgileTutorialOneQuery,
        queryRef as PreloadedQuery<
            agileTutorialOneQuery,
            Record<string, unknown>
        >
    );
    return {
        title: data.tutorialDetailsCollection?.items[0]?.tutorialRef?.title,
        description:
            data.tutorialDetailsCollection?.items[0]?.tutorialRef?.description
    };
};
